import { DateTime } from 'luxon';

export interface LocalMoment {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
}

export interface ResolvedMoment {
    /** Milliseconds since the epoch (UTC). */
    utcMs: number;
    /** Offset from UTC in minutes that applied at that place and moment (east is positive). */
    offsetMinutes: number;
    /** The local time falls in a daylight-saving gap and never happened; the clock-forward reading is used. */
    gap: boolean;
    /** The local time happened twice (clocks went back); the first (daylight-saving) one is used. */
    ambiguous: boolean;
}

const offsetAt = (ms: number, zone: string): number =>
    DateTime.fromMillis(ms, { zone }).offset;

export const isValidZone = (zone: string): boolean =>
    DateTime.local().setZone(zone).isValid;

/**
 * Turns a wall-clock time at a place into a UTC instant using the historical tz database, so past
 * daylight-saving rules and zone changes apply. Throws if the zone or the date does not exist.
 */
export const resolveMoment = (
    local: LocalMoment,
    zone: string
): ResolvedMoment => {
    if (!isValidZone(zone)) throw new Error(`Unknown time zone: ${zone}`);
    const asUtc = Date.UTC(
        local.year,
        local.month - 1,
        local.day,
        local.hour,
        local.minute
    );
    const check = new Date(asUtc);
    if (
        check.getUTCFullYear() !== local.year ||
        check.getUTCMonth() !== local.month - 1 ||
        check.getUTCDate() !== local.day
    ) {
        throw new Error('That calendar date does not exist');
    }
    // Candidate offsets: the ones in force a day before and a day after the wall-clock time.
    const dayBefore = offsetAt(asUtc - 86_400_000, zone);
    const dayAfter = offsetAt(asUtc + 86_400_000, zone);
    const offsets =
        dayBefore === dayAfter ? [dayBefore] : [dayBefore, dayAfter];
    const valid = offsets
        .map((offset) => ({ offset, ms: asUtc - offset * 60_000 }))
        .filter(({ offset, ms }) => offsetAt(ms, zone) === offset)
        .sort((a, b) => a.ms - b.ms);

    if (valid.length === 0) {
        // Skipped by the clock change: read it with the offset from before the change.
        const before = offsetAt(asUtc - 86_400_000, zone);
        return {
            utcMs: asUtc - before * 60_000,
            offsetMinutes: before,
            gap: true,
            ambiguous: false,
        };
    }
    const chosen = valid[0];
    return {
        utcMs: chosen.ms,
        offsetMinutes: chosen.offset,
        gap: false,
        ambiguous: valid.length > 1,
    };
};
